// src/pages/PrimarySport.tsx
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthLayout from "@/components/AuthLayout";
import ProgressBar from "@/components/ProgressBar";
import { getSupabase, currentUser } from "@/lib/supabase";
import { markCompleted, getStepIndexById, steps } from "@/lib/onboarding";

const sports = [
  "Running",
  "Cycling",
  "Swimming",   
  "Triathlon",
  "CrossFit",
  "Hyrox",
  "Functional Fitness",
  "Football",
  "Rugby",
  "Tennis",
  "Gym",
  "Rowing",
];

export default function PrimarySport() {
  const [sel, setSel] = useState("");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleNext = async () => {
    if (!sel) return;
    setSaving(true);
    setErr(null);

    try {
      const user = await currentUser();
      if (!user) throw new Error("No user found — please log in again.");

      const supabase = getSupabase();
      if (!supabase) throw new Error("Supabase client not initialized.");

      const { error } = await supabase
        .from("clients")
        .update({ primary_sport: sel })
        .eq("user_id", user.id);

      if (error) throw error;

      markCompleted("primarySport");

      const nextIdx = getStepIndexById("primarySport") + 1;
      const nextPath = steps[nextIdx]?.path || "/secondary-sport";

      navigate(nextPath);
    } catch (e: any) {
      setErr(e.message ?? "Failed to save primary sport.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AuthLayout>
      <div className="absolute top-4 left-4 z-10">
  <button
    onClick={() => navigate(-1)}
    className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-500 hover:text-[#0f172a] transition-colors"
  >
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
    </svg>
    Back
  </button>
</div>
      
      <div className="space-y-6 text-center">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#0f172a]">Primary Sport</h1>
          <p className="text-sm sm:text-base text-gray-500 mt-2">
            Choose your main sport or training focus.
          </p>
        </div>

        {/* Responsive pill grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 max-w-2xl mx-auto">
          {sports.map((sport) => (
            <button
              key={sport}
              onClick={() => setSel(sport)}
              className={`
                h-14 rounded-full text-base font-semibold transition-all duration-200
                flex items-center justify-center px-6
                ${sel === sport
                  ? "bg-[#a78bfa] text-[#0f172a] shadow-lg shadow-[#a78bfa]/30 ring-2 ring-[#a78bfa]/50"
                  : "bg-gray-800 text-[#0f172a] border border-gray-700 hover:bg-gray-700 hover:border-gray-600"
                }
                active:scale-95 focus:outline-none focus:ring-2 focus:ring-[#a78bfa]/50
              `}
            >
              {sport}
            </button>
          ))}
        </div>

        <button
          onClick={handleNext}
          disabled={!sel || saving}
          className={`
            w-full max-w-sm mx-auto bg-[#a78bfa] hover:bg-[#8b5cf6] text-[#0f172a] font-bold
            rounded-2xl py-4 text-lg transition-all duration-200
            active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg
            ${!sel || saving ? "opacity-60" : ""}
          `}
        >
          {saving ? "Saving..." : "Next"}
        </button>

        {err && <p className="text-sm text-red-400">{err}</p>}

        <div className="my-6 border-t border-gray-800" />
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <span className="w-2 h-2 bg-[#a78bfa] rounded-full" />
          Input
        </div>

        <footer className="text-center text-xs text-gray-500">
          © 2025 Input Performance Ltd. All rights reserved.
        </footer>

        <div className="mt-6">
          <ProgressBar step={5} total={10} />
        </div>
      </div>
    </AuthLayout>
  );
}
